import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell as CelulaRecharts,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ReferenceLine,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Activity,
  ArrowDown,
  ArrowUp,
  BarChart3,
  Gauge,
  Minus,
  TrendingUp,
  Truck,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

import { ChartContainer, ChartTooltip, type ChartConfig } from '@/ui/base/chart';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Skeleton } from '@/ui/base/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { cn } from '@/nucleo/utilitarios';

import {
  fmtDinheiroCurto,
  fmtInt,
  fmtKmCurto,
  formatarMesCurto,
  quantil,
  totaisRegime,
  variacaoMensal,
} from './calculos';
import type { DadosIndicadoresMensais, DreIndicadores, PontoSerieMensal } from './tipos';

/**
 * As 3 "cenas" da narrativa visual do mês, portadas do protótipo de
 * interface. Cor de série sempre por variável CSS de tema — nunca hsl fixo —
 * para responder ao tema escuro e ao modo de alto contraste.
 */

const CONFIG_PF_PJ: ChartConfig = {
  pf: { label: 'Pessoa física', color: 'hsl(var(--primary))' },
  pj: { label: 'Pessoa jurídica', color: 'hsl(var(--orange))' },
};

// ────────────────────────────────────────────────────────────────────────────
// ATO 1 — o retrato do mês (KPIs com tendência)
// ────────────────────────────────────────────────────────────────────────────

interface KpiComTendencia {
  chave: string;
  rotulo: string;
  valor: string;
  dica: string;
  variacao: number | null;
  serie: { mes: string; valor: number }[];
  Icone: LucideIcon;
}

function SeloVariacao({ valor }: { valor: number | null }) {
  if (valor === null || !Number.isFinite(valor)) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <Minus className="h-3 w-3" /> —
      </span>
    );
  }
  const subiu = valor > 0;
  const estavel = Math.abs(valor) < 0.001;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-xs font-medium tabular-nums',
        estavel ? 'text-muted-foreground' : subiu ? 'text-success' : 'text-destructive',
      )}
    >
      {estavel ? (
        <Minus className="h-3 w-3" />
      ) : subiu ? (
        <ArrowUp className="h-3 w-3" />
      ) : (
        <ArrowDown className="h-3 w-3" />
      )}
      {estavel ? '—' : `${(Math.abs(valor) * 100).toFixed(1)}%`}
    </span>
  );
}

function CartaoKpi({ kpi }: { kpi: KpiComTendencia }) {
  const config: ChartConfig = useMemo(
    () => ({ valor: { label: kpi.rotulo, color: 'hsl(var(--primary))' } }),
    [kpi.rotulo],
  );
  return (
    <Card className="rounded-2xl">
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">{kpi.rotulo}</p>
            <p className="text-2xl font-bold mt-1 truncate tabular-nums">{kpi.valor}</p>
            <div className="flex items-center gap-2 mt-1">
              <SeloVariacao valor={kpi.variacao} />
              <span className="text-xs text-muted-foreground">vs. mês anterior</span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">{kpi.dica}</p>
          </div>
          <kpi.Icone className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
        </div>
        <ChartContainer
          config={config}
          className="h-12 w-full mt-2 aspect-auto"
          role="img"
          aria-label={`Tendência de ${kpi.rotulo} nos últimos meses`}
        >
          <LineChart data={kpi.serie} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
            <ChartTooltip
              cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeDasharray: '3 3' }}
              contentStyle={{
                background: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: 8,
                fontSize: 11,
                padding: '4px 8px',
              }}
              // Sem XAxis, o Recharts entrega o índice do ponto como rótulo, não o mês.
              labelFormatter={(_, payload) => {
                const p = payload?.[0]?.payload as KpiComTendencia['serie'][number] | undefined;
                return p ? formatarMesCurto(p.mes) : '';
              }}
              formatter={(valor: number) => [valor >= 1000 ? fmtInt(valor) : valor, kpi.rotulo]}
            />
            <Line
              type="monotone"
              dataKey="valor"
              stroke="var(--color-valor)"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 3, fill: 'var(--color-valor)' }}
              isAnimationActive={false}
            />
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

function Ato1Retrato({ serie, carregando }: { serie: PontoSerieMensal[]; carregando: boolean }) {
  const ultimo = serie[serie.length - 1];
  const kpis: KpiComTendencia[] = ultimo
    ? [
        {
          chave: 'brutoComInss',
          rotulo: 'Bruto + INSS',
          valor: fmtDinheiroCurto(ultimo.brutoComInss),
          dica: `INSS PF: ${fmtDinheiroCurto(ultimo.inss)}`,
          variacao: variacaoMensal(serie, 'brutoComInss'),
          serie: serie.map((p) => ({ mes: p.mes, valor: p.brutoComInss })),
          Icone: Wallet,
        },
        {
          chave: 'criancas',
          rotulo: 'Crianças transportadas',
          valor: fmtInt(ultimo.criancas),
          dica: `${fmtKmCurto(ultimo.kmTotal)} no mês`,
          variacao: variacaoMensal(serie, 'criancas'),
          serie: serie.map((p) => ({ mes: p.mes, valor: p.criancas })),
          Icone: Users,
        },
        {
          chave: 'veiculos',
          rotulo: 'Frota ativa',
          valor: fmtInt(ultimo.veiculos),
          dica: 'Veículos vinculados no mês',
          variacao: variacaoMensal(serie, 'veiculos'),
          serie: serie.map((p) => ({ mes: p.mes, valor: p.veiculos })),
          Icone: Truck,
        },
        {
          chave: 'kmTotal',
          rotulo: 'KM total rodado',
          valor: fmtKmCurto(ultimo.kmTotal),
          dica: `${fmtInt(ultimo.condutores)} condutores ativos`,
          variacao: variacaoMensal(serie, 'kmTotal'),
          serie: serie.map((p) => ({ mes: p.mes, valor: p.kmTotal })),
          Icone: Gauge,
        },
      ]
    : [];

  return (
    <Card className="rounded-2xl">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-primary" aria-hidden="true" />O retrato do mês
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Indicadores-chave da competência e tendência dos últimos {serie.length || 6} meses.
        </p>
      </CardHeader>
      <CardContent>
        {carregando || !ultimo ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-2xl" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {kpis.map((k) => (
              <CartaoKpi key={k.chave} kpi={k} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// ATO 2 — quem pesa no mês (composição PF × PJ por DRE)
// ────────────────────────────────────────────────────────────────────────────

type MetricaAto2 = 'bruto' | 'criancas' | 'frota' | 'km';

const METRICAS_ATO2: { valor: MetricaAto2; rotulo: string; formatar: (n: number) => string }[] = [
  { valor: 'bruto', rotulo: 'Bruto (R$)', formatar: (n) => fmtDinheiroCurto(n) },
  { valor: 'criancas', rotulo: 'Crianças transportadas', formatar: (n) => fmtInt(n) },
  { valor: 'frota', rotulo: 'Veículos ativos', formatar: (n) => fmtInt(n) },
  { valor: 'km', rotulo: 'KM total', formatar: (n) => `${fmtInt(n)} km` },
];

function extrairPfPj(r: DreIndicadores, metrica: MetricaAto2): { pf: number; pj: number } {
  const pf = totaisRegime(r.pf, true);
  const pj = totaisRegime(r.pj, false);
  switch (metrica) {
    case 'bruto':
      return { pf: pf.bruto, pj: pj.bruto };
    case 'criancas':
      return { pf: pf.criancasTotal, pj: pj.criancasTotal };
    case 'frota':
      return { pf: pf.veiculosTotal, pj: pj.veiculosTotal };
    case 'km':
      return { pf: pf.kmTotal, pj: pj.kmTotal };
  }
}

function Ato2Composicao({
  dados,
  carregando,
}: {
  dados: DadosIndicadoresMensais | null;
  carregando: boolean;
}) {
  const [metrica, setMetrica] = useState<MetricaAto2>('bruto');
  const conf = METRICAS_ATO2.find((m) => m.valor === metrica)!;

  const dadosGrafico = useMemo(() => {
    if (!dados) return [];
    return dados.porDre
      .map((r) => {
        const { pf, pj } = extrairPfPj(r, metrica);
        return { dre: r.dreCodigo, dreNome: r.dreNome, pf, pj, total: pf + pj };
      })
      .sort((a, b) => b.total - a.total);
  }, [dados, metrica]);

  const totais = useMemo(
    () =>
      dadosGrafico.reduce(
        (acc, r) => {
          acc.pf += r.pf;
          acc.pj += r.pj;
          return acc;
        },
        { pf: 0, pj: 0 },
      ),
    [dadosGrafico],
  );

  const totalGeral = totais.pf + totais.pj;
  const pctPf = totalGeral ? (totais.pf / totalGeral) * 100 : 0;
  const pctPj = totalGeral ? (totais.pj / totalGeral) * 100 : 0;

  const dadosDonut = [
    { name: 'pf', rotulo: 'PF', value: totais.pf },
    { name: 'pj', rotulo: 'PJ', value: totais.pj },
  ];

  return (
    <Card className="rounded-2xl">
      <CardHeader className="pb-3 gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-primary" aria-hidden="true" />
              Quem pesa no mês
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Composição PF × PJ por DRE — ordenado por total decrescente.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span
              className="text-xs text-muted-foreground whitespace-nowrap"
              id="rotulo-metrica-ato2"
            >
              Métrica:
            </span>
            <Select value={metrica} onValueChange={(v) => setMetrica(v as MetricaAto2)}>
              <SelectTrigger className="h-9 w-[200px]" aria-labelledby="rotulo-metrica-ato2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {METRICAS_ATO2.map((m) => (
                  <SelectItem key={m.valor} value={m.valor}>
                    {m.rotulo}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {carregando || dadosGrafico.length === 0 ? (
          <Skeleton className="h-[420px] rounded-xl" />
        ) : (
          <div className="grid gap-4 lg:grid-cols-[1fr_220px]">
            <ChartContainer
              config={CONFIG_PF_PJ}
              className="h-[420px] w-full aspect-auto"
              role="img"
              aria-label={`Comparação de ${conf.rotulo.toLowerCase()} por DRE, pessoa física contra pessoa jurídica`}
            >
              <BarChart
                data={dadosGrafico}
                layout="vertical"
                margin={{ top: 4, right: 24, left: 8, bottom: 0 }}
                barCategoryGap={4}
              >
                <CartesianGrid
                  horizontal={false}
                  strokeDasharray="3 3"
                  stroke="hsl(var(--muted-foreground))"
                  strokeOpacity={0.4}
                />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v: number) =>
                    metrica === 'bruto' ? fmtDinheiroCurto(v).replace('R$ ', '') : fmtInt(v)
                  }
                />
                <YAxis
                  type="category"
                  dataKey="dre"
                  tick={{ fontSize: 11, fill: 'hsl(var(--foreground))' }}
                  axisLine={false}
                  tickLine={false}
                  width={48}
                />
                <Tooltip
                  cursor={{ fill: 'hsl(var(--muted) / 0.3)' }}
                  contentStyle={{
                    background: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  formatter={(valor: number, nome: string) => [
                    conf.formatar(Number(valor)),
                    nome === 'pf' ? 'PF' : 'PJ',
                  ]}
                  labelFormatter={(_, payload) => {
                    const p = payload?.[0]?.payload as { dre: string; dreNome: string } | undefined;
                    return p ? `${p.dre} — ${p.dreNome}` : '';
                  }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 11 }}
                  formatter={(v) => (v === 'pf' ? 'PF' : 'PJ')}
                />
                <Bar
                  dataKey="pf"
                  name="pf"
                  stackId="reg"
                  fill="var(--color-pf)"
                  radius={[4, 0, 0, 4]}
                />
                <Bar
                  dataKey="pj"
                  name="pj"
                  stackId="reg"
                  fill="var(--color-pj)"
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ChartContainer>

            <div className="flex flex-col items-center justify-center gap-2 border rounded-xl p-3">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Mix do mês</p>
              <ChartContainer
                config={CONFIG_PF_PJ}
                className="h-[160px] w-full aspect-auto"
                role="img"
                aria-label={`Mix pessoa física e pessoa jurídica: ${pctPf.toFixed(1)}% PF, ${pctPj.toFixed(1)}% PJ`}
              >
                <PieChart>
                  <Pie
                    data={dadosDonut}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                    stroke="none"
                  >
                    {dadosDonut.map((d) => (
                      <CelulaRecharts key={d.name} fill={`var(--color-${d.name})`} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    formatter={(valor: number, _nome, item) => {
                      const p = item?.payload as { rotulo: string } | undefined;
                      return [conf.formatar(Number(valor)), p?.rotulo ?? ''];
                    }}
                  />
                </PieChart>
              </ChartContainer>
              <div className="w-full space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-1.5">
                    <span
                      className="h-2.5 w-2.5 rounded-sm"
                      style={{ background: 'hsl(var(--primary))' }}
                    />
                    PF
                  </span>
                  <span className="tabular-nums font-medium">{pctPf.toFixed(1)}%</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-1.5">
                    <span
                      className="h-2.5 w-2.5 rounded-sm"
                      style={{ background: 'hsl(var(--orange))' }}
                    />
                    PJ
                  </span>
                  <span className="tabular-nums font-medium">{pctPj.toFixed(1)}%</span>
                </div>
                <div className="border-t pt-1 mt-2 flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Total</span>
                  <span className="tabular-nums font-semibold">{conf.formatar(totalGeral)}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// ATO 3 — eficiência operacional (dispersão + mediana/percentis)
// ────────────────────────────────────────────────────────────────────────────

interface PontoRazao {
  dre: string;
  dreNome: string;
  valor: number;
}

function DispersaoRazao({
  titulo,
  unidade,
  dados,
  formatar,
}: {
  titulo: string;
  unidade: string;
  dados: PontoRazao[];
  formatar: (n: number) => string;
}) {
  const valores = dados.map((d) => d.valor).filter((v) => Number.isFinite(v) && v > 0);
  const mediana = quantil(valores, 0.5);
  const p10 = quantil(valores, 0.1);
  const p90 = quantil(valores, 0.9);

  const pontos = dados.map((d, i) => ({
    ...d,
    y: 0.5 + (i % 2) * 0.05 - 0.025,
    foraDaFaixa: d.valor > 0 && (d.valor < p10 || d.valor > p90),
  }));

  const config: ChartConfig = { valor: { label: titulo, color: 'hsl(var(--primary))' } };

  return (
    <div className="border rounded-xl p-3 space-y-2">
      <div>
        <p className="text-xs text-muted-foreground uppercase tracking-wider">{titulo}</p>
        <p className="text-lg font-bold tabular-nums">
          {formatar(mediana)}{' '}
          <span className="text-xs font-normal text-muted-foreground">{unidade} · mediana</span>
        </p>
      </div>
      <ChartContainer
        config={config}
        className="h-[120px] w-full aspect-auto"
        role="img"
        aria-label={`Distribuição de ${titulo} entre as DREs, mediana ${formatar(mediana)}`}
      >
        <ScatterChart margin={{ top: 8, right: 12, bottom: 22, left: 12 }}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="hsl(var(--muted-foreground))"
            strokeOpacity={0.4}
            vertical
            horizontal={false}
          />
          <XAxis
            type="number"
            dataKey="valor"
            tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v: number) => formatar(v).replace('R$ ', '')}
            domain={['auto', 'auto']}
          />
          <YAxis type="number" dataKey="y" hide domain={[0, 1]} />
          <ReferenceLine
            x={mediana}
            stroke="hsl(var(--primary))"
            strokeDasharray="4 2"
            strokeWidth={1.5}
          />
          <ReferenceLine x={p10} stroke="hsl(var(--muted-foreground))" strokeDasharray="2 4" />
          <ReferenceLine x={p90} stroke="hsl(var(--muted-foreground))" strokeDasharray="2 4" />
          <Tooltip
            cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeDasharray: '3 3' }}
            contentStyle={{
              background: 'hsl(var(--card))',
              border: '1px solid hsl(var(--border))',
              borderRadius: 8,
              fontSize: 12,
            }}
            formatter={(valor: number, nome: string, item) => {
              if (nome === 'y') return [null, null] as unknown as [string, string];
              const p = item?.payload as PontoRazao | undefined;
              return [formatar(Number(valor)), p?.dre ?? ''];
            }}
            labelFormatter={() => ''}
          />
          <Scatter data={pontos} fill="hsl(var(--primary))">
            {pontos.map((p) => (
              <CelulaRecharts
                key={p.dre}
                fill={p.foraDaFaixa ? 'hsl(var(--warning))' : 'hsl(var(--primary))'}
              />
            ))}
          </Scatter>
        </ScatterChart>
      </ChartContainer>
      <p className="text-[10px] text-muted-foreground">
        Cada ponto = 1 DRE · linha sólida = mediana · pontilhadas = P10/P90 · destaque = fora da
        faixa
      </p>
    </div>
  );
}

interface PontosEficiencia {
  rsCrianca: PontoRazao[];
  rsKm: PontoRazao[];
  criancasVeiculo: PontoRazao[];
}

function Ato3Eficiencia({
  dados,
  carregando,
}: {
  dados: DadosIndicadoresMensais | null;
  carregando: boolean;
}) {
  const pontos = useMemo<PontosEficiencia>(() => {
    if (!dados) return { rsCrianca: [], rsKm: [], criancasVeiculo: [] };
    const rsCrianca: PontoRazao[] = [];
    const rsKm: PontoRazao[] = [];
    const criancasVeiculo: PontoRazao[] = [];
    for (const r of dados.porDre) {
      const pf = totaisRegime(r.pf, true);
      const pj = totaisRegime(r.pj, false);
      const bruto = pf.bruto + pf.inss + pj.bruto;
      const criancas = pf.criancasTotal + pj.criancasTotal;
      const km = pf.kmTotal + pj.kmTotal;
      const veiculos = pf.veiculosTotal + pj.veiculosTotal;
      if (criancas > 0)
        rsCrianca.push({ dre: r.dreCodigo, dreNome: r.dreNome, valor: bruto / criancas });
      if (km > 0) rsKm.push({ dre: r.dreCodigo, dreNome: r.dreNome, valor: bruto / km });
      if (veiculos > 0)
        criancasVeiculo.push({ dre: r.dreCodigo, dreNome: r.dreNome, valor: criancas / veiculos });
    }
    return { rsCrianca, rsKm, criancasVeiculo };
  }, [dados]);

  return (
    <Card className="rounded-2xl">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Activity className="h-4 w-4 text-primary" aria-hidden="true" />
          Eficiência operacional
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Distribuição entre as DREs — destaque automático para quem opera acima ou abaixo da faixa
          típica (P10–P90).
        </p>
      </CardHeader>
      <CardContent>
        {carregando || !dados ? (
          <div className="grid gap-3 md:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-[200px] rounded-xl" />
            ))}
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-3">
            <DispersaoRazao
              titulo="R$ por criança"
              unidade="R$ / criança"
              dados={pontos.rsCrianca}
              formatar={(n) => fmtDinheiroCurto(n)}
            />
            <DispersaoRazao
              titulo="R$ por km"
              unidade="R$ / km"
              dados={pontos.rsKm}
              formatar={(n) => fmtDinheiroCurto(n)}
            />
            <DispersaoRazao
              titulo="Crianças por veículo"
              unidade="crianças / veículo"
              dados={pontos.criancasVeiculo}
              formatar={(n) => n.toFixed(1)}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Wrapper público
// ────────────────────────────────────────────────────────────────────────────

export function IndicadoresMensaisCharts({
  dados,
  serie,
  carregandoMes,
  carregandoSerie,
}: {
  dados: DadosIndicadoresMensais | null;
  serie: PontoSerieMensal[];
  carregandoMes: boolean;
  carregandoSerie: boolean;
}) {
  return (
    <div className="space-y-6">
      <Ato1Retrato serie={serie} carregando={carregandoSerie} />
      <Ato2Composicao dados={dados} carregando={carregandoMes} />
      <Ato3Eficiencia dados={dados} carregando={carregandoMes} />
    </div>
  );
}
